import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  DialogTitle,
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../widgets/Modal';
import Input from '../../../widgets/Input';
import Button from '../../../widgets/Button';
import { useMfaModalContext } from '../../../../shared/providers/MfaModalProvider';

import { authenticate, verifyCode } from '../../../../shared/service/auth';
import {
  FACTORS,
  MfaModalState,
  VerifyToken,
} from '../../../../shared/constants/MFA';

import HelpCircle from '../../../../assets/icons/mfa/help-circle.svg?react';
import PolygonIcon from '../../../../assets/icons/mfa/polygonIcon.svg?react';
import { addItemToLocalstorage } from '../../../../utilities/common/storage';
import { StorageItems } from '../../../../shared/constants/app';
import { validateCode } from '../../../../utilities/common/validateCode';

function AuthCodeModal({ data, enabled }: { data: any; enabled: boolean }) {
  const [invalidAuthCode, setInvalidAuthCode] = useState<boolean>(false);
  const [invalidToken, setInvalidToken] = useState<boolean>(false);

  const { t } = useTranslation();

  const inputLabel = t('ENTER_YOUR_CODE') || '';

  const {
    setActiveModal,
    stateToken,
    setAccessToken,
    promptAlertModal,
    setPromptAlertModal,
    setLogoutModal,
    supportMessageSent,
    tfaCode,
    setTfaCode,
    setAllowAccess,
  } = useMfaModalContext();

  const verifyCodeArgs = {
    token: data.token,
    stateToken: stateToken || '',
    authCode: tfaCode,
    factorType: FACTORS.TOTP,
  };

  const { mutate: refetchMfaActiveCheck, isPending: authenticateLoading } =
    useMutation({
      mutationKey: ['checkMfaActiveNew2'],
      mutationFn: () => authenticate(data.token),
      onSuccess: () => {
        refetchMfaVerifyAuthCode();
      },
    });

  const { mutate: refetchMfaVerifyAuthCode, isPending: verifyCodeLoading } =
    useMutation({
      mutationKey: ['mfaVerifyAuthCodeNew2'],
      mutationFn: () => verifyCode(verifyCodeArgs),
      onSuccess: (response: any) => {
        setAccessToken(response?.data?.accessToken);
        addItemToLocalstorage(
          StorageItems.ACCESS_TOKEN,
          response?.data?.accessToken,
        );
        setAllowAccess({ login: true, layout: true });
        if (response?.data?.accessToken) {
          if (data) {
            if ('keyUri' in data) {
              setActiveModal(MfaModalState.Enabled2Fa);
              setAllowAccess({ login: false, layout: false });
            } else {
              setActiveModal(MfaModalState.NoModal);
              setAllowAccess({ login: true, layout: true });
            }
          }
        }
      },
      onError: (response: { data: any }) => {
        if (response?.data?.override?.code == VerifyToken.INVALID_AUTH_CODE) {
          setInvalidAuthCode(true);
        }
        if (response?.data?.override?.code == VerifyToken.INVALID_STATE_TOKEN) {
          setInvalidToken(true);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      },
    });

  const handleVerifyCode = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInvalidAuthCode(false);
    setInvalidToken(false);
    refetchMfaActiveCheck();
  };

  return (
    <Modal
      open={enabled}
      closeButton
      handleClose={() => {
        !promptAlertModal && setLogoutModal(true);
      }}
      modalClassName="max-w-[500px] w-full px-[32px] py-[26px] pt-[20px] intl-2fa-modal"
    >
      <div>
        <DialogTitle
          as="h4"
          className="text-black-900 mb-2 text-lg font-semibold"
        >
          {t('MFA_INPUT_CODE_HEADER')}
        </DialogTitle>
        <span className="text-black-600 mb-[10px] text-xs">
          {t('MFA_INPUT_CODE_SUBHEADER')}
        </span>
      </div>
      <div className="grid text-center">
        <form className="mt-2 flex-col" onSubmit={handleVerifyCode}>
          <div className="mt-5 mb-[153px] grid text-left">
            <Input
              name="tfaCode"
              id="tfaCode"
              label={inputLabel}
              autoComplete="off"
              inputClass={` ${
                (invalidAuthCode || invalidToken) &&
                'focus:shadow-[0px_0px_0px_4px_#FEE4E2] !border-redd-600'
              }`}
              value={tfaCode}
              onChange={(e) => setTfaCode(validateCode(e, 6))}
            />
            {invalidAuthCode && (
              <p className="text-redd-600 mt-1 text-sm leading-6">
                {t('MFA_INVALID_CODE')}
              </p>
            )}
            {invalidToken && (
              <p className="text-redd-600 mt-1 text-sm leading-6">
                {t('MFA_INVALID_TOKEN')}
              </p>
            )}
            <div className="mt-3 flex w-full">
              <p className="text-black-600 w-fit text-sm">
                {t('MFA_USER_INFO')}
              </p>
              <div className="relative flex-1">
                <Popover className="relative">
                  {() => (
                    <>
                      <PopoverButton className="focus:outline-hidden">
                        <HelpCircle className="[&>*]:stroke-black-600 ml-2" />
                      </PopoverButton>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <PopoverPanel className="absolute top-3 left-[-16px] z-[-1] transform">
                          <PolygonIcon className="fill-black-800 relative bottom-[-10px] left-5 w-5" />

                          <div className="bg-black-800 rounded-[8px] px-4 py-[10px] text-[13px] text-white">
                            {t('MFA_USER_INFO_TOAST')}
                          </div>
                        </PopoverPanel>
                      </Transition>
                    </>
                  )}
                </Popover>
              </div>
            </div>
          </div>
          <div className="relative mt-8">
            <div className="mb-6 flex items-center justify-center gap-x-2">
              <span className="text-black-600 text-right text-[13px]">
                {t('MFA_NEED_HELP')}
              </span>

              <span
                onClick={() => setPromptAlertModal(true)}
                className="text-primary-600 cursor-pointer text-[13px] font-semibold"
              >
                {t('CONTACT_SUPPORT')}
              </span>
            </div>

            <Transition
              as={Fragment}
              show={supportMessageSent}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <div className="bg-black-800 absolute top-[-85px] right-0 left-0 m-auto flex w-fit justify-center rounded-[8px] px-4 py-[10px] text-[13px] text-white">
                {t('CONTACT_SUPPORT_TOAST')}
              </div>
            </Transition>

            <Button
              type="submit"
              size="lg"
              disabled={!tfaCode}
              isLoading={verifyCodeLoading || authenticateLoading}
            >
              {t('AUTHENTICATE')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default AuthCodeModal;
